/**
 * Notifications API services with real-time SSE support
 */
import { api } from './api';
import type { ApiResponse } from '@/types';

// Helper to convert snake_case to camelCase
function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

// Transform object keys from snake_case to camelCase
function transformKeys<T>(obj: unknown): T {
  if (obj === null || obj === undefined) {
    return obj as T;
  }
  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeys(item)) as T;
  }
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const camelKey = snakeToCamel(key);
      result[camelKey] = transformKeys(value);
    }
    return result as T;
  }
  return obj as T;
}

// ============================================================================
// SSE Event Types
// ============================================================================

/**
 * Real-time notification event types for SSE
 */
export type SSEEventType =
  | 'workflow:pending'
  | 'document:updated'
  | 'pfile:movement'
  | 'system:announcement'
  | 'notification:new'
  | 'notification:read'
  | 'connected'
  | 'heartbeat';

/**
 * SSE event callback function type
 */
export type SSEEventCallback = (data: unknown) => void;

/**
 * SSE connection state
 */
export type SSEConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

// ============================================================================
// SSE Client Class
// ============================================================================

/**
 * Server-Sent Events client for real-time notifications
 */
export class NotificationSSEClient {
  private eventSource: EventSource | null = null;
  private listeners: Map<string, Set<SSEEventCallback>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 1000; // Start with 1 second
  private maxReconnectDelay = 30000; // Max 30 seconds
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionState: SSEConnectionState = 'disconnected';
  private stateListeners: Set<(state: SSEConnectionState) => void> = new Set();
  private baseUrl = '/api/sse/notifications';

  /**
   * Get current connection state
   */
  getConnectionState(): SSEConnectionState {
    return this.connectionState;
  }

  /**
   * Subscribe to connection state changes
   */
  onStateChange(callback: (state: SSEConnectionState) => void): () => void {
    this.stateListeners.add(callback);
    return () => {
      this.stateListeners.delete(callback);
    };
  }

  /**
   * Update connection state and notify listeners
   */
  private setConnectionState(state: SSEConnectionState): void {
    this.connectionState = state;
    this.stateListeners.forEach((listener) => listener(state));
  }

  /**
   * Establish SSE connection with auth token
   */
  connect(): void {
    // Don't connect if already connected or connecting
    if (
      this.eventSource &&
      (this.eventSource.readyState === EventSource.CONNECTING ||
        this.eventSource.readyState === EventSource.OPEN)
    ) {
      return;
    }

    // Get auth token from localStorage
    const token = localStorage.getItem('accessToken');
    if (!token) {
      console.warn('[SSE] No auth token available, skipping connection');
      return;
    }

    // Clear any existing reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.setConnectionState(
      this.reconnectAttempts > 0 ? 'reconnecting' : 'connecting'
    );

    // Build URL with auth token as query parameter
    // (EventSource doesn't support custom headers)
    const url = `${this.baseUrl}?token=${encodeURIComponent(token)}`;

    try {
      this.eventSource = new EventSource(url, { withCredentials: true });

      this.eventSource.onopen = () => {
        console.log('[SSE] Connection established');
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.setConnectionState('connected');
        this.emit('connected', { timestamp: Date.now() });
      };

      this.eventSource.onerror = (error) => {
        console.error('[SSE] Connection error:', error);

        // Connection was closed
        if (this.eventSource?.readyState === EventSource.CLOSED) {
          this.eventSource = null;
          this.setConnectionState('disconnected');
          this.scheduleReconnect();
        }
      };

      // Listen for standard message events
      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const eventType = data.type || 'message';
          this.emit(eventType, data);
        } catch (err) {
          console.error('[SSE] Failed to parse message:', err);
        }
      };

      // Register listeners for specific event types
      const eventTypes: SSEEventType[] = [
        'workflow:pending',
        'document:updated',
        'pfile:movement',
        'system:announcement',
        'notification:new',
        'notification:read',
        'heartbeat',
      ];

      eventTypes.forEach((eventType) => {
        this.eventSource?.addEventListener(eventType, (event) => {
          try {
            const data = JSON.parse((event as MessageEvent).data);
            this.emit(eventType, data);
          } catch (err) {
            console.error(`[SSE] Failed to parse ${eventType} event:`, err);
          }
        });
      });
    } catch (err) {
      console.error('[SSE] Failed to create EventSource:', err);
      this.setConnectionState('disconnected');
      this.scheduleReconnect();
    }
  }

  /**
   * Close SSE connection
   */
  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    this.reconnectAttempts = 0;
    this.setConnectionState('disconnected');
    console.log('[SSE] Connection closed');
  }

  /**
   * Subscribe to specific event types
   */
  subscribe(eventType: string, callback: SSEEventCallback): void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(callback);
  }

  /**
   * Unsubscribe from specific event types
   */
  unsubscribe(eventType: string, callback: SSEEventCallback): void {
    const eventListeners = this.listeners.get(eventType);
    if (eventListeners) {
      eventListeners.delete(callback);
      if (eventListeners.size === 0) {
        this.listeners.delete(eventType);
      }
    }
  }

  /**
   * Emit event to all subscribed listeners
   */
  private emit(eventType: string, data: unknown): void {
    const eventListeners = this.listeners.get(eventType);
    if (eventListeners) {
      eventListeners.forEach((callback) => {
        try {
          callback(data);
        } catch (err) {
          console.error(`[SSE] Error in ${eventType} listener:`, err);
        }
      });
    }

    // Also emit to wildcard listeners
    const wildcardListeners = this.listeners.get('*');
    if (wildcardListeners) {
      wildcardListeners.forEach((callback) => {
        try {
          callback({ type: eventType, data });
        } catch (err) {
          console.error('[SSE] Error in wildcard listener:', err);
        }
      });
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[SSE] Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    console.log(
      `[SSE] Scheduling reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`
    );

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * Force reconnection (useful after auth token refresh)
   */
  reconnect(): void {
    this.disconnect();
    this.reconnectAttempts = 0;
    this.connect();
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return (
      this.eventSource !== null &&
      this.eventSource.readyState === EventSource.OPEN
    );
  }
}

// Singleton instance for the SSE client
export const notificationSSE = new NotificationSSEClient();

// Notification types
export type NotificationType =
  | 'workflow_assigned'
  | 'workflow_approved'
  | 'workflow_rejected'
  | 'workflow_delegated'
  | 'record_created'
  | 'record_updated'
  | 'task_due'
  | 'system_announcement'
  // Compliance alerts (Phase 3.3.1)
  | 'compliance_retention_due'
  | 'compliance_review_required'
  | 'compliance_hold_placed'
  | 'compliance_hold_released'
  // Security alerts (Phase 3.3.3)
  | 'security_access_violation'
  | 'security_suspicious_activity'
  | 'security_login_failed'
  | 'security_permission_changed'
  // AI/Anomaly alerts
  | 'anomaly_detected'
  | 'anomaly_resolved';

export interface Notification {
  id: string;
  userId: string;
  mdaId: string;
  type: NotificationType;
  title: string;
  message: string;
  data: Record<string, unknown> | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

export interface PaginatedMeta {
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

export interface PaginatedApiResponse<T> extends ApiResponse<T> {
  meta?: PaginatedMeta;
}

export interface NotificationFilters {
  page?: number;
  perPage?: number;
  type?: NotificationType;
  isRead?: boolean;
}

// Helper for paginated GET with transformation
async function transformedGetWithMeta<T>(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>
): Promise<PaginatedApiResponse<T>> {
  const response = await api.get<unknown>(endpoint, params);
  if (response.success && response.data) {
    const rawMeta = response.meta as PaginatedMeta | undefined;
    return {
      ...response,
      data: transformKeys<T>(response.data),
      meta: rawMeta,
    };
  }
  return response as PaginatedApiResponse<T>;
}

async function transformedGet<T>(endpoint: string): Promise<ApiResponse<T>> {
  const response = await api.get<unknown>(endpoint);
  if (response.success && response.data) {
    return {
      ...response,
      data: transformKeys<T>(response.data),
    };
  }
  return response as ApiResponse<T>;
}

// Notifications API
export const notificationsApi = {
  /**
   * Get user notifications with pagination and filters
   */
  list: (params?: NotificationFilters) =>
    transformedGetWithMeta<Notification[]>(
      '/v1/notifications',
      params as Record<string, string | number | boolean | undefined>
    ),

  /**
   * Get unread notification count
   */
  getUnreadCount: () => transformedGet<{ count: number }>('/v1/notifications/unread-count'),

  /**
   * Mark a notification as read
   */
  markAsRead: async (id: string): Promise<ApiResponse<Notification>> => {
    const response = await api.put<unknown>(`/v1/notifications/${id}/read`, {});
    if (response.success && response.data) {
      return { ...response, data: transformKeys<Notification>(response.data) };
    }
    return response as ApiResponse<Notification>;
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async (): Promise<ApiResponse<{ message: string }>> => {
    const response = await api.put<unknown>('/v1/notifications/read-all', {});
    if (response.success && response.data) {
      return { ...response, data: transformKeys<{ message: string }>(response.data) };
    }
    return response as ApiResponse<{ message: string }>;
  },

  /**
   * Delete a notification
   */
  delete: (id: string) => api.delete(`/v1/notifications/${id}`),

  /**
   * Delete all read notifications
   */
  deleteAllRead: () => api.delete('/v1/notifications/read'),
};

/**
 * Get notification icon based on type
 */
export function getNotificationIcon(type: NotificationType): string {
  switch (type) {
    case 'workflow_assigned':
      return 'task';
    case 'workflow_approved':
      return 'check-circle';
    case 'workflow_rejected':
      return 'x-circle';
    case 'workflow_delegated':
      return 'users';
    case 'record_created':
      return 'file-plus';
    case 'record_updated':
      return 'file-edit';
    case 'task_due':
      return 'clock';
    case 'system_announcement':
      return 'megaphone';
    // Compliance icons
    case 'compliance_retention_due':
      return 'calendar-clock';
    case 'compliance_review_required':
      return 'clipboard-check';
    case 'compliance_hold_placed':
      return 'lock';
    case 'compliance_hold_released':
      return 'unlock';
    // Security icons
    case 'security_access_violation':
      return 'shield-alert';
    case 'security_suspicious_activity':
      return 'alert-triangle';
    case 'security_login_failed':
      return 'user-x';
    case 'security_permission_changed':
      return 'key';
    // AI/Anomaly icons
    case 'anomaly_detected':
      return 'scan';
    case 'anomaly_resolved':
      return 'check-circle-2';
    default:
      return 'bell';
  }
}

/**
 * Get notification color based on type
 */
export function getNotificationColor(type: NotificationType): string {
  switch (type) {
    case 'workflow_assigned':
      return '#3B82F6'; // blue
    case 'workflow_approved':
      return '#10B981'; // green
    case 'workflow_rejected':
      return '#EF4444'; // red
    case 'workflow_delegated':
      return '#8B5CF6'; // purple
    case 'record_created':
      return '#10B981'; // green
    case 'record_updated':
      return '#F59E0B'; // amber
    case 'task_due':
      return '#EF4444'; // red
    case 'system_announcement':
      return '#6366F1'; // indigo
    // Compliance colors
    case 'compliance_retention_due':
      return '#F97316'; // orange
    case 'compliance_review_required':
      return '#EAB308'; // yellow
    case 'compliance_hold_placed':
      return '#DC2626'; // red-600
    case 'compliance_hold_released':
      return '#22C55E'; // green-500
    // Security colors
    case 'security_access_violation':
      return '#DC2626'; // red-600
    case 'security_suspicious_activity':
      return '#F97316'; // orange
    case 'security_login_failed':
      return '#EF4444'; // red
    case 'security_permission_changed':
      return '#8B5CF6'; // purple
    // AI/Anomaly colors
    case 'anomaly_detected':
      return '#F59E0B'; // amber
    case 'anomaly_resolved':
      return '#10B981'; // green
    default:
      return '#6B7280'; // gray
  }
}

/**
 * Get notification category for grouping/filtering
 */
export function getNotificationCategory(type: NotificationType): 'workflow' | 'records' | 'compliance' | 'security' | 'system' {
  switch (type) {
    case 'workflow_assigned':
    case 'workflow_approved':
    case 'workflow_rejected':
    case 'workflow_delegated':
    case 'task_due':
      return 'workflow';
    case 'record_created':
    case 'record_updated':
      return 'records';
    case 'compliance_retention_due':
    case 'compliance_review_required':
    case 'compliance_hold_placed':
    case 'compliance_hold_released':
      return 'compliance';
    case 'security_access_violation':
    case 'security_suspicious_activity':
    case 'security_login_failed':
    case 'security_permission_changed':
    case 'anomaly_detected':
    case 'anomaly_resolved':
      return 'security';
    case 'system_announcement':
    default:
      return 'system';
  }
}

/**
 * Get notification priority (for sorting)
 */
export function getNotificationPriority(type: NotificationType): 'critical' | 'high' | 'medium' | 'low' {
  switch (type) {
    case 'security_access_violation':
    case 'security_suspicious_activity':
    case 'compliance_hold_placed':
      return 'critical';
    case 'anomaly_detected':
    case 'security_login_failed':
    case 'compliance_retention_due':
    case 'workflow_rejected':
    case 'task_due':
      return 'high';
    case 'workflow_assigned':
    case 'workflow_delegated':
    case 'compliance_review_required':
    case 'security_permission_changed':
      return 'medium';
    default:
      return 'low';
  }
}

/**
 * Format notification time as relative time
 */
export function formatNotificationTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
    return 'Just now';
  }
  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
}
